import { useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Container,
  Group,
  List,
  Paper,
  Select,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconPrinter, IconRefresh, IconTestPipe } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import { ApiError, api } from "../api/client";
import type { GodexStatus, Shipment } from "../api/types";
import { useLocalAgent } from "../hooks/useLocalAgent";
import { localAgentPrint } from "../api/localAgent";
import { BackgroundPicker, loadStoredBackground } from "../components/labelEditor/BackgroundPicker";

export function Godex() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<GodexStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [printing, setPrinting] = useState(false);

  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [shipmentId, setShipmentId] = useState<string | null>(null);
  const [boxNumber, setBoxNumber] = useState("");
  const localAgent = useLocalAgent();

  const [bgDevice, setBgDevice] = useState<string>(() => loadStoredBackground("device"));
  const [bgBox, setBgBox] = useState<string>(() => loadStoredBackground("box"));

  async function refresh() {
    setLoading(true);
    try {
      setStatus(await api.godexStatus());
    } catch {
      notifications.show({ color: "red", message: t("godex.statusLoadFailed") });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
    api.listShipments().then(setShipments).catch(() => undefined);
  }, []);

  async function handleTestPrint() {
    setPrinting(true);
    try {
      if (localAgent.godex) {
        const r = await api.godexPrintTestPayload();
        await localAgentPrint(r.ezpl_b64, localAgent.selectedId);
        notifications.show({
          color: "green",
          title: t("godex.sentTitle"),
          message: t("godex.testPrintViaAgent"),
        });
      } else {
        const r = await api.godexPrintTest(bgDevice);
        notifications.show({
          color: "green",
          title: t("godex.sentTitle"),
          message: t("godex.testPrintSent", { bytes: r.bytes, backend: r.backend, target: r.target }),
        });
      }
    } catch (err) {
      const msg =
        err instanceof ApiError &&
        typeof err.detail === "object" &&
        err.detail !== null &&
        "detail" in err.detail
          ? String((err.detail as Record<string, unknown>).detail)
          : err instanceof Error
            ? err.message
            : t("common.error");
      notifications.show({
        color: "red",
        title: t("godex.printFailedTitle"),
        message: msg,
      });
    } finally {
      setPrinting(false);
    }
  }

  async function handlePrintBox() {
    if (!shipmentId || !boxNumber.trim()) return;
    setPrinting(true);
    try {
      if (localAgent.godex) {
        const r = await api.godexPrintBoxPayload(Number(shipmentId), boxNumber.trim());
        await localAgentPrint(r.ezpl_b64, localAgent.selectedId);
      } else {
        await api.godexPrintBox(Number(shipmentId), boxNumber.trim(), bgBox);
      }
      notifications.show({
        color: "green",
        title: t("godex.boxLabelSentTitle"),
        message: t("godex.boxLabelSentMsg", { bytes: "–", target: "" }),
      });
    } catch (err) {
      const msg =
        err instanceof ApiError &&
        typeof err.detail === "object" &&
        err.detail !== null &&
        "detail" in err.detail
          ? String((err.detail as Record<string, unknown>).detail)
          : err instanceof Error
            ? err.message
            : t("common.error");
      notifications.show({
        color: "red",
        title: t("godex.printFailedTitle"),
        message: msg,
      });
    } finally {
      setPrinting(false);
    }
  }

  function statusBadge() {
    // When the local agent is connected it is the real print path — show ITS
    // configured target (the IP the user edits in the agent), not the backend env.
    if (localAgent.available && localAgent.godex_target) {
      return (
        <Badge color={localAgent.godex ? "green" : "yellow"}>
          {t("godex.agentTarget", { target: localAgent.godex_target })}
        </Badge>
      );
    }
    if (loading) return <Badge color="gray">{t("godex.statusLoading")}</Badge>;
    if (!status) return <Badge color="red">{t("godex.statusUnknown")}</Badge>;
    if (!status.configured) return <Badge color="red">{t("godex.statusUnconfigured")}</Badge>;
    if (status.backend === "tcp") return <Badge color="green">TCP — {status.target}</Badge>;
    if (status.backend === "win_spooler" && status.printer_present)
      return <Badge color="green">Windows — {status.target}</Badge>;
    if (status.backend === "linux_usb" && status.device_present)
      return <Badge color="green">Linux USB — {status.target}</Badge>;
    return <Badge color="yellow">{status.backend} — {status.target}</Badge>;
  }

  const shipmentOptions = shipments.map((s) => ({
    value: String(s.id),
    label: t("godex.shipmentOption", { code: s.code, count: s.device_count }),
  }));

  return (
    <Container size="xl">
      <Group justify="space-between" mb="md">
        <Title order={2}>{t("godex.title")}</Title>
        <Button variant="subtle" leftSection={<IconRefresh size={14} />} onClick={refresh}>
          {t("godex.refresh")}
        </Button>
      </Group>

      <Paper withBorder p="lg" mb="md">
        <Group justify="space-between">
          <Group>
            <IconPrinter size={28} />
            <div>
              <Text fw={500}>{t("godex.statusLabel")}</Text>
              {statusBadge()}
              {localAgent.available && (
                <Badge
                  variant="dot"
                  color={localAgent.godex ? "green" : "yellow"}
                  ml="xs"
                >
                  {localAgent.godex ? t("godex.agentGodexOk") : t("godex.agentGodexUnreachable")}
                </Badge>
              )}
            </div>
          </Group>
          <Group>
            <BackgroundPicker labelType="device" value={bgDevice} onChange={setBgDevice} />
            <Button
              leftSection={<IconTestPipe size={16} />}
              onClick={handleTestPrint}
              loading={printing}
            >
              {t("godex.printTest")}
            </Button>
          </Group>
        </Group>

        {status?.backend === "win_spooler" && status.available_printers && (
          <List size="xs" mt="md" c="dimmed">
            <Text size="xs" mb={4}>
              {t("godex.availablePrinters")}
            </Text>
            {status.available_printers.map((p) => (
              <List.Item key={p}>{p}</List.Item>
            ))}
          </List>
        )}

        {status?.error && (
          <Alert color="red" mt="md">
            {status.error}
          </Alert>
        )}
      </Paper>

      <Paper withBorder p="lg">
        <Title order={4} mb="sm">
          {t("godex.printBoxLabelTitle")}
        </Title>
        <Text c="dimmed" size="sm" mb="md">
          {t("godex.boxLabelDesc")}
        </Text>
        <Group align="flex-end">
          <Select
            label={t("godex.shipmentLabel")}
            data={shipmentOptions}
            value={shipmentId}
            onChange={setShipmentId}
            placeholder={t("godex.shipmentPlaceholder")}
            searchable
            w={320}
            maw="100%"
          />
          <TextInput
            label="NR.CUTIE"
            value={boxNumber}
            onChange={(e) => setBoxNumber(e.currentTarget.value)}
            placeholder="2600s"
            w={160}
          />
          <BackgroundPicker labelType="box" value={bgBox} onChange={setBgBox} />
          <Button
            onClick={handlePrintBox}
            disabled={!shipmentId || !boxNumber.trim()}
            loading={printing}
            leftSection={<IconPrinter size={16} />}
          >
            {t("godex.print")}
          </Button>
        </Group>
        <Text size="xs" c="dimmed" mt="xs">
          {t("godex.modulesPerLabelHint")}
        </Text>
      </Paper>
    </Container>
  );
}
